import {
  isHabitFulfilled,
  isScheduledDate,
  type Habit,
  type HabitStats,
} from '@cleartask/shared';
import { StreakBar } from './StreakBar.js';

export interface HabitRowProps {
  habit: Habit;
  stats: HabitStats | undefined;
  dates: string[];
  values: Map<string, number>;
  today: string;
  onLog: (value: number) => void;
  onEdit: () => void;
  onArchive: () => void;
}

/**
 * Um habito na lista (spec 9.5).
 *
 * O controle de hoje muda com o tipo de meta: `boolean` ganha caixa de marcar,
 * `count` ganha campo numerico com a unidade. Um checkbox para "20 paginas"
 * transformaria a meta em decoracao — marcar significaria "li alguma coisa".
 *
 * Quando hoje NAO e dia previsto nao ha controle nenhum, so a informacao. Um
 * checkbox ali gravaria um registro que a regra de streak ignora, e um controle
 * cujo efeito e invisivel e pior que a ausencia dele.
 */
export function HabitRow({
  habit,
  stats,
  dates,
  values,
  today,
  onLog,
  onEdit,
  onArchive,
}: HabitRowProps) {
  const valorDeHoje = values.get(today);
  const cumpridoHoje = valorDeHoje !== undefined && isHabitFulfilled(habit, valorDeHoje);
  const previstoHoje = isScheduledDate(habit.daysMask, today);

  const streak = stats?.currentStreak ?? 0;

  return (
    <li className="rounded bg-surface p-3 shadow-card">
      <div className="flex items-center gap-3">
        {habit.icon ? (
          <span aria-hidden="true" className="text-md">
            {habit.icon}
          </span>
        ) : null}

        <span className="min-w-0 flex-1 truncate font-medium">{habit.name}</span>

        {streak > 0 ? (
          <span
            className="shrink-0 text-sm text-text-mut"
            // O 🔥 e decorativo; o numero e a informacao.
            aria-label={`Sequência atual de ${streak} ${streak === 1 ? 'dia' : 'dias'}`}
          >
            <span aria-hidden="true">🔥 </span>
            <span className="font-mono">{streak}</span>
          </span>
        ) : null}

        {previstoHoje ? (
          <ControleDeHoje
            habit={habit}
            cumprido={cumpridoHoje}
            valor={valorDeHoje}
            onLog={onLog}
          />
        ) : (
          <span className="shrink-0 text-sm text-text-mut">não previsto hoje</span>
        )}
      </div>

      <div className="mt-2 flex items-center justify-between gap-3">
        <StreakBar habit={habit} dates={dates} values={values} />

        <div className="flex shrink-0 items-center">
          {/*
            Nome, meta, dias, icone e cor so podiam ser escolhidos na criacao:
            errar a meta significava arquivar o habito e comecar outro, jogando
            fora o historico junto. A API ja aceitava o PATCH desde a fase 6.
          */}
          <button
            type="button"
            onClick={onEdit}
            aria-label={`Editar ${habit.name}`}
            className="min-h-touch rounded-sm px-2 text-sm text-text-mut hover:bg-surface-2"
          >
            Editar
          </button>

          <button
            type="button"
            onClick={onArchive}
            className="min-h-touch rounded-sm px-2 text-sm text-text-mut hover:bg-surface-2"
          >
            Arquivar
          </button>
        </div>
      </div>
    </li>
  );
}

function ControleDeHoje({
  habit,
  cumprido,
  valor,
  onLog,
}: {
  habit: Habit;
  cumprido: boolean;
  valor: number | undefined;
  onLog: (value: number) => void;
}) {
  if (habit.goalType === 'count') {
    return (
      <label className="flex shrink-0 items-center gap-2 text-sm">
        <input
          type="number"
          min={0}
          max={10_000}
          value={valor ?? ''}
          aria-label={`Hoje — ${habit.name}`}
          onChange={(e) => onLog(e.target.value === '' ? 0 : Number(e.target.value))}
          className="min-h-touch w-20 rounded-sm border border-border bg-bg px-2 text-right font-mono"
        />
        <span className="text-text-mut">
          /{habit.goalTarget}
          {habit.unit ? ` ${habit.unit}` : ''}
        </span>
      </label>
    );
  }

  return (
    <label className="flex min-h-touch shrink-0 cursor-pointer items-center gap-2 text-sm">
      <input
        type="checkbox"
        checked={cumprido}
        aria-label={`Hoje — ${habit.name}`}
        onChange={(e) => onLog(e.target.checked ? 1 : 0)}
        className="h-5 w-5 accent-brand-teal"
      />
      <span className="text-text-mut">hoje</span>
    </label>
  );
}
